































import Vue from 'vue'
import PatientStatsCommon from '@/components/patient-stats/PatientStatsCommon.vue'
import PatientStatsTab from '@/components/patient-stats/PatientStatsTab.vue'
import PatientStatsDetailed from '@/components/patient-stats/PatientStatsDetailed.vue'
import PatientStatsExercise from '@/components/patient-stats/PatientStatsExercise.vue'
import PatientStatsMapping from '@/components/patient-stats/PatientStatsMapping.vue'
import PatientStatsNotes from '@/components/patient-stats/PatientStatsNotes.vue'
import PatientStatsSummary from '@/components/patient-stats/PatientStatsSummary.vue'
import PatientStatsActigraphy from '@/components/patient-stats/PatientStatsActigraphy.vue'
import { PATIENT_STATS_PAGE_TYPES } from '@/utils/constants'
import moment from 'moment-timezone'
import { ActionNames, GetterNames } from '@/store/modules/patient/keys'
import { mapActions, mapGetters, mapState } from 'vuex'
import { ModuleNames } from '@/store/module-names'
import { removeAbbr, USER_TIME_ZONE } from '@/utils/time'
import { withSingularOrPluralLabel } from '@/utils/labels'
import { CHART_DATE_FORMAT } from '@/components/patient-stats/constants'
import { FlatPatient } from '@/store/modules/patient/getters'
import { PATIENT_STATS_PAGE_CONFIGS, PatientStatsPageConfig, PatientStatsPageType } from '@/constants/patient-stats'
import { USER_ROLE_TYPES } from '@/store/modules/app/types'
import { CUSTOM_PATIENT_STATS_PAGE_TYPE } from '@/constants/app-variables'

const weekLabels = ['WEEK', 'WEEKS']

type ComponentData = {
  isInitialized: boolean
  startTimestamp: number
  endTimestamp: number
  queryComponentMap: { [propName: string]: string }
  periodOptions: Array<{ weeks: number; label: string }>
  displayWeeks: number
  patientStatsPageType: PatientStatsPageConfig
}

export default Vue.extend({
  components: {
    PatientStatsCommon,
    PatientStatsTab,
    PatientStatsDetailed,
    PatientStatsExercise,
    PatientStatsMapping,
    PatientStatsNotes,
    PatientStatsSummary,
    PatientStatsActigraphy,
  },
  data(): ComponentData {
    return {
      isInitialized: false,
      startTimestamp: 0,
      endTimestamp: 0,
      queryComponentMap: Object.values(PATIENT_STATS_PAGE_TYPES).reduce((acc, value) => {
        acc[value.key] = 'patient-stats-' + value.key
        return acc
      }, {}),
      periodOptions: [
        { weeks: 1, label: withSingularOrPluralLabel(1, weekLabels[0], weekLabels[1]) },
        { weeks: 2, label: withSingularOrPluralLabel(2, weekLabels[0], weekLabels[1]) },
        { weeks: 3, label: withSingularOrPluralLabel(3, weekLabels[0], weekLabels[1]) },
        { weeks: 4, label: withSingularOrPluralLabel(4, weekLabels[0], weekLabels[1]) },
        { weeks: 6, label: withSingularOrPluralLabel(6, weekLabels[0], weekLabels[1]) },
        { weeks: 8, label: withSingularOrPluralLabel(8, weekLabels[0], weekLabels[1]) },
        { weeks: 10, label: withSingularOrPluralLabel(10, weekLabels[0], weekLabels[1]) },
        { weeks: 12, label: withSingularOrPluralLabel(12, weekLabels[0], weekLabels[1]) },
        { weeks: Infinity, label: 'ALL' },
      ],
      displayWeeks: 0,
      patientStatsPageType: PATIENT_STATS_PAGE_CONFIGS[CUSTOM_PATIENT_STATS_PAGE_TYPE || PatientStatsPageType.DEFAULT],
    }
  },
  computed: {
    ...mapState(ModuleNames.app, ['userRoles']),
    ...mapGetters(ModuleNames.patient, [GetterNames.patients]),
    patientId(): string {
      return this.$route.params.patient_id || ''
    },
    currentPatient(): FlatPatient | null {
      return this[GetterNames.patients].find(patient => patient.id === this.patientId)
    },
    patientTimezone(): string {
      return this.currentPatient ? this.currentPatient.timezone || USER_TIME_ZONE : USER_TIME_ZONE
    },
  },
  async mounted(): Promise<void> {
    await this.initComponent({ patientId: this.patientId, path: this.$route.path })
  },
  async beforeRouteUpdate(to, from, next): Promise<void> {
    await this.initComponent({ patientId: to.params.patient_id, path: to.path })
    next()
  },
  created() {
    let userRoles = this?.userRoles ?? []

    if (CUSTOM_PATIENT_STATS_PAGE_TYPE) {
      // custom for nursing home
      this.patientStatsPageType = PATIENT_STATS_PAGE_CONFIGS[CUSTOM_PATIENT_STATS_PAGE_TYPE]
    } else if (userRoles.includes(USER_ROLE_TYPES.MULTI_DEV_DASH)) {
      this.patientStatsPageType = PATIENT_STATS_PAGE_CONFIGS[PatientStatsPageType.MULTI_DEV_DASH]
    } else if (userRoles.includes(USER_ROLE_TYPES.MULTI_DEV_LIMITED_DASH)) {
      this.patientStatsPageType = PATIENT_STATS_PAGE_CONFIGS[PatientStatsPageType.MULTI_DEV_LIMITED_DASH]
    } else if (userRoles.includes(USER_ROLE_TYPES.ACTIVITY_DASH)) {
      this.patientStatsPageType = PATIENT_STATS_PAGE_CONFIGS[PatientStatsPageType.ACTIVITY_DASH]
    }
  },
  methods: {
    ...mapActions(ModuleNames.patient, [ActionNames.getPatient]),
    async initComponent({ patientId, path }): Promise<void> {
      this.isInitialized = false
      this.startTimestamp = 0
      this.endTimestamp = 0

      let currentPatient = this[GetterNames.patients].find(patient => patient.id === patientId)

      if (!currentPatient) {
        await this[ActionNames.getPatient]({ patientId: this.patientId })
        currentPatient = this[GetterNames.patients].find(patient => patient.id === patientId)
      }

      if (!currentPatient) {
        await this.$router.replace({ name: '404', params: { '0': path } })
        return
      }

      const start = moment()
        .utc()
        .tz(this.patientTimezone)
        .add(this.patientStatsPageType.defaultDisplayWeeks * -1, 'weeks')
        .add(1, 'day')
        .startOf('day')
      this.startTimestamp = start.valueOf()

      const end = moment()
        .utc()
        .tz(this.patientTimezone)
        .endOf('day')
      this.endTimestamp = end.valueOf()
      this.isInitialized = true
    },
    selectPeriodOption(option): void {
      this.displayWeeks = option.weeks
      if (option.weeks === Infinity) {
        this.startTimestamp = 1
        return
      }

      const start = moment(this.endTimestamp)
        .add(-option.weeks, 'weeks')
        .add(1, 'day')
        .tz(this.patientTimezone)
        .startOf('day')
      this.startTimestamp = start.valueOf()
    },
  },
})
